// src/pages/admin/PageInfraKeys.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";

const SOURCES = ["Tailscale", "GitHub", "TrueNAS", "AWS", "Google", "Other"];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function ExpiryBadge({ dateStr }) {
  if (!dateStr) return <span style={{ color: "#9ca3af", fontSize: 12 }}>No expiry</span>;
  const days = daysUntil(dateStr);
  const [y, m, d] = dateStr.split("-");
  const display = `${d}/${m}/${y}`;
  if (days < 0)  return <span style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>Expired {display}</span>;
  if (days <= 14) return <span style={{ background: "#fef9c3", color: "#b45309", borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>⚠ {display} ({days}d)</span>;
  if (days <= 30) return <span style={{ background: "#fff7ed", color: "#c2410c", borderRadius: 4, padding: "2px 8px", fontSize: 12 }}>{display} ({days}d)</span>;
  return <span style={{ background: "#f0fdf4", color: "#15803d", borderRadius: 4, padding: "2px 8px", fontSize: 12 }}>{display} ({days}d)</span>;
}

const EMPTY = { name: "", source: "Tailscale", expiresAt: "", notes: "" };

export function PageInfraKeys() {
  const [keys,    setKeys]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [form,    setForm]    = useState(null); // null = closed, {} = new, {id,...} = edit
  const [saving,  setSaving]  = useState(false);

  useEffect(() => { load(); }, []);

  function load() {
    setLoading(true);
    api.fetchInfraKeys()
      .then(setKeys)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.saveInfraKey(form);
      setForm(null);
      load();
    } catch (e) {
      alert("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this key record?")) return;
    await api.deleteInfraKey(id);
    setKeys(prev => prev.filter(k => k.id !== id));
  }

  // Sort: expired first, then by days until expiry ascending, then no-expiry last
  const sorted = [...keys].sort((a, b) => {
    const da = a.expires_at ? daysUntil(a.expires_at) : 99999;
    const db = b.expires_at ? daysUntil(b.expires_at) : 99999;
    return da - db;
  });

  const expiring = keys.filter(k => k.expires_at && daysUntil(k.expires_at) <= 30 && daysUntil(k.expires_at) >= 0);
  const expired  = keys.filter(k => k.expires_at && daysUntil(k.expires_at) < 0);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Infrastructure Keys</div>
          <div className="page-subtitle">Track API keys, auth tokens and their expiry dates</div>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY })}>+ Add Key</button>
        </div>
      </div>

      {/* Alert banners */}
      {expired.length > 0 && (
        <div className="alert-warn" style={{ marginBottom: 12, fontWeight: 600 }}>
          ⛔ {expired.length} key{expired.length !== 1 ? "s" : ""} expired — renewal required: {expired.map(k => k.name).join(", ")}
        </div>
      )}
      {expiring.length > 0 && (
        <div style={{ background: "#fefce8", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#92400e" }}>
          ⚠ {expiring.length} key{expiring.length !== 1 ? "s" : ""} expiring within 30 days: {expiring.map(k => k.name).join(", ")}
        </div>
      )}

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Key / Token Name</th>
                <th>Source</th>
                <th>Expiry</th>
                <th>Notes</th>
                <th>Last Updated</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: "center", color: "#9ca3af", padding: 32 }}>No keys tracked yet. Add one above.</td></tr>
              )}
              {sorted.map(k => (
                <tr key={k.id}>
                  <td style={{ fontWeight: 600 }}>{k.name}</td>
                  <td>
                    <span style={{ fontSize: 11, background: "#f1f5f9", borderRadius: 3, padding: "2px 7px", color: "#475569" }}>
                      {k.source}
                    </span>
                  </td>
                  <td><ExpiryBadge dateStr={k.expires_at} /></td>
                  <td style={{ fontSize: 12, color: "#6b7280", maxWidth: 300 }}>{k.notes || "—"}</td>
                  <td style={{ fontSize: 11, color: "#9ca3af" }}>
                    {k.updated_at ? new Date(k.updated_at).toLocaleDateString("en-AE") : "—"}
                  </td>
                  <td style={{ display: "flex", gap: 6 }}>
                    <button className="btn-small" onClick={() => setForm({
                      id: k.id, name: k.name, source: k.source,
                      expiresAt: k.expires_at ?? "", notes: k.notes ?? "",
                    })}>Edit</button>
                    <button className="btn-small btn-danger" onClick={() => handleDelete(k.id)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {form && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setForm(null); }}>
          <div className="modal" style={{ width: 440 }}>
            <div className="modal-header">
              <span className="modal-title">{form.id ? "Edit Key" : "Add Key"}</span>
              <button className="modal-close" onClick={() => setForm(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="form-label">Key / Token Name *</label>
                <input className="form-input" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Tailscale auth key (prod server)" />
              </div>
              <div>
                <label className="form-label">Source</label>
                <select className="form-input" value={form.source}
                  onChange={e => setForm(f => ({ ...f, source: e.target.value }))}>
                  {SOURCES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Expiry Date (leave blank if no expiry)</label>
                <input className="form-input" type="date" value={form.expiresAt}
                  onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Notes</label>
                <input className="form-input" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Where to regenerate, who has access, etc." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
